import toastr from 'toastr';
import { reactive } from 'vue';
import { multipleSelectGetSelects, multipleSelectSetSelects } from '../multiple-select-scripts';
import { buildDataIdSelector } from './add-file-utils';
import type { Client, MultipleSelectClient } from './add-file.types';
import { ClientContext } from './available-clients';
import { setClientTextToSummaryTextLine } from './form-clients';
import { getAvailableClients, getDataIdAttribute, joinFormIdAndSelector, populateRecipientsList } from './index';

export const selectionOrder = reactive<Record<string, string[]>>({});

export const populateNewFileDocumentClients = (formId: string) => {
    $(formId)
        .find('select.document-recipients-select')
        .each((_index, element) => {
            const dataId = getDataIdAttribute(element);
            if (dataId) {
                const selector = joinFormIdAndSelector(formId, dataId);
                populateRecipientsList(selector, ClientContext.NEW);
            }
        });
    $(formId)
        .find('select.document-signers-select')
        .each((_index, element) => {
            populateDocumentSignersList(element, ClientContext.NEW);
        });
};

export const populateDocumentSignersList = (signersElement: HTMLElement, clientContext: ClientContext) => {
    const signersDataId = getDataIdAttribute(signersElement);
    const signersSelector = signersDataId && buildDataIdSelector(signersDataId);
    const availableClients = getAvailableClients(clientContext);
    let selectedRecipients: string[];
    const recipientsDataId = getDataIdAttribute(
        $(signersElement).parents('.document-item').find('select.document-recipients-select')[0],
    );
    const recipientsSelector = recipientsDataId && buildDataIdSelector(recipientsDataId);
    // If there is no recipients selector (e.g. only signers in form), use all Clients
    if (recipientsSelector) {
        selectedRecipients = multipleSelectGetSelects(recipientsSelector);
    } else {
        // Keep only the 'value' of Clients to match the multipleSelectGetSelects format
        selectedRecipients = $.map(availableClients, client => client['value']);
    }
    if (!signersSelector || !signersDataId) return;

    const alreadySelectedSigners = multipleSelectGetSelects(signersSelector).map(Number);
    let availableSigners = availableClients.filter(item => selectedRecipients.includes(item.value?.toString() ?? ''));

    availableSigners = setClientTextToSummaryTextLine(availableSigners);

    multipleSelectSetSelects(signersSelector, availableSigners, alreadySelectedSigners);

    refreshSelectionOrder(signersDataId);

    clearESignEditFieldsIfSignersChanged(signersSelector, alreadySelectedSigners, availableSigners);
};

export const refreshSelectionOrder = (signersDataId: string) => {
    if (typeof selectionOrder[signersDataId] === 'undefined') {
        selectionOrder[signersDataId] = [];
    }

    const selectedItems = multipleSelectGetSelects(buildDataIdSelector(signersDataId));
    // Update selectionOrder with selected items
    selectionOrder[signersDataId] = $.grep(selectionOrder[signersDataId], function (value) {
        return $.inArray(value, selectedItems) !== -1;
    });
    updateSignerSelections(signersDataId);
};

export const updateSignerSelections = (signersDataId: string) => {
    // Re-label signer selections
    $('[data-selection-order-group="' + signersDataId + '"]').each((_key, signerSelector) => {
        $(signerSelector).html('');
        const selectionOrderIdx = $.inArray($(signerSelector).data('id').toString(), selectionOrder[signersDataId]);
        if (selectionOrderIdx !== -1) {
            $(signerSelector).html(' (signer ' + (selectionOrderIdx + 1) + ')');
        }
    });
};

const clearESignEditFieldsIfSignersChanged = (
    documentSignersSelector: string,
    alreadySelectedSigners: number[],
    availableSigners: MultipleSelectClient[],
) => {
    const availableSignerIds = $.map(availableSigners, client => Number(client.value));

    const changeInSigners = !alreadySelectedSigners.every(signer => availableSignerIds.indexOf(signer) >= 0);

    if (changeInSigners) {
        clearESignEditFields(documentSignersSelector);
    }
};

export const clearESignEditFields = (documentSignersSelector: string) => {
    const $documentContainer = $(documentSignersSelector).closest('div.document-container');

    if ($documentContainer.find('input.document-sign-edit').val() === '') {
        // Do nothing if editField is already clear
        return;
    }

    // Clear the eSign edit fields (folderId, editURL)
    $documentContainer.find('.esign-edit-url').data('url', '');
    $documentContainer.find('input.document-sign-edit').val('');

    const $requestSignatureCheckbox = $documentContainer.find('input.document-sign-checkbox');
    if ($requestSignatureCheckbox.is(':checked')) {
        // If 'Request eSignature' checkbox was still checked, change it and inform user
        $requestSignatureCheckbox.click();

        if ($documentContainer.find('input.document-checkbox').is(':checked')) {
            // Only show a warning if the document is still 'active' (i.e. box is checked)
            const documentName = $documentContainer.find('input.document-checkbox').data('filename');
            toastr.warning(
                'Request eSignature(s) was automatically deselected for the following document: <br><b>' + documentName + '</b>',
                'Signer changed',
                {
                    closeButton: true,
                    timeOut: 0,
                    extendedTimeOut: 0,
                    closeDuration: 300,
                },
            );
        }
    }
};

export const handleEmailChangeForSigners = (clientId: Client['id'] = '') => {
    $.each($('select.document-signers-select'), (_idx, signersSelectElement) => {
        const signerIDs = $(signersSelectElement).val();
        const clientIDInSignerIDs = Array.isArray(signerIDs) && signerIDs.includes(clientId);
        if (clientIDInSignerIDs) {
            clearESignEditFields(buildDataIdSelector($(signersSelectElement).data('id')));
        }
    });
};
